import React from 'react';
import classnames from 'classnames';
import ReactModal from 'react-modal';

import styles from './Modal.module.scss';

// NOTE: This is using https://github.com/reactjs/react-modal package
// Documentation: https://reactcommunity.org/react-modal
// More properties to be passed into the modal can be found here https://reactcommunity.org/react-modal/#usage

ReactModal.setAppElement('#root');

interface ModalProps {
  /** @any - Contents of the modal. To be used when nesting contents in the parent. */
  children?: any;
  /** @any - Contents of the modal. To be used when passing in as a param. */
  content?: any;
  /** @boolean - describing if the modal should be shown or not. Defaults to false. */
  isOpen: boolean;
  /** @any -  Function that will be run when the modal is requested to be closed (either by clicking on overlay or pressing ESC). Note: It is not called if isOpen is changed by other means. */
  onRequestClose: any;
  /** @any - Function that will be run after the modal has opened. */
  onAfterOpen?: any;
  /** @any - Function that will be run after the modal has closed. */
  onAfterClose?: any;
  /** @number - indicating the milliseconds to wait before closing the modal. */
  closeTimeoutMS?: number;
  /** @any - indicating styles to be used for the modal. It has two keys, `overlay` and `content`. See the `Styles` section for more details. */
  style?: any;
  /** @string - indicating how the content container should be announced to screenreaders */
  contentLabel?: any;
  /** @string - className to be applied to the portal. See the `Styles` section for more details. */
  portalClassName?: any;
  /** @string - className to be applied to the overlay. See the `Styles` section for more details. */
  overlayClassName?: any;
  /** @string -  id to be applied to the content div. */
  id?: string;
  /** @string - className to be applied to the modal content. See the `Styles` section for more details. */
  className?: string;
  /** @string - className to be applied to the document.body (must be a constant string). This attribute when set as `null` doesn't add any class  to document.body. See the `Styles` section for more details. */
  bodyOpenClassName?: string;
  /** @string - className to be applied to the document.html (must be a constant string). This attribute is `null` by default. See the `Styles` section for more details. */
  htmlOpenClassName?: string;
  /** @boolean - indicating if the appElement should be hidden */
  ariaHideApp?: boolean;
  /** @boolean - indicating if the modal should be focused after render. */
  shouldFocusAfterRender?: boolean;
  /** @boolean - indicating if the overlay should close the modal */
  shouldCloseOnOverlayClick?: boolean;
  /** @boolean -indicating if pressing the esc key should close the modal. Note: By disabling the esc key from closing the modal you may introduce an accessibility issue. */
  shouldCloseOnEsc?: boolean;
  /** @boolean - indicating if the modal should restore focus to the element that had focus prior to its display. */
  shouldReturnFocusAfterClose?: boolean;
  /** @string - indicating the role of the modal, allowing the 'dialog' role to be applied if desired. This attribute is `dialog` by default. */
  role?: string;
  /** @any - Function that will be called to get the parent element that the modal will be attached to. */
  parentSelector?: any;
  /** @any - Additional aria attributes (optional). */
  aria?: any;
  /** @any - Additional data attributes (optional). */
  data?: any;
}

export const Modal: React.FC<ModalProps> = ({
  children,
  content,
  isOpen,
  onRequestClose,
  onAfterOpen,
  onAfterClose,
  closeTimeoutMS,
  style,
  contentLabel,
  portalClassName,
  overlayClassName,
  id,
  className,
  bodyOpenClassName,
  htmlOpenClassName,
  ariaHideApp,
  shouldFocusAfterRender,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
  shouldReturnFocusAfterClose,
  role,
  parentSelector,
  aria,
  data,
}) => {
  return (
    <ReactModal
      id={id}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      className={classnames(className, styles.Modal)}
      overlayClassName={classnames(overlayClassName, styles.Overlay)}
      closeTimeoutMS={closeTimeoutMS}
      style={style}
      contentLabel={contentLabel}
      portalClassName={classnames(portalClassName)}
      bodyOpenClassName={classnames(bodyOpenClassName)}
      htmlOpenClassName={classnames(htmlOpenClassName)}
      ariaHideApp={ariaHideApp}
      shouldFocusAfterRender={shouldFocusAfterRender}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      role={role}
      parentSelector={parentSelector}
      aria={aria}
      data={data}
    >
      {content}
      {children}
    </ReactModal>
  );
};
